import { ALGOLIA_RULE_CONTEXT } from './constants';

// Tablets are matched first: every tablet user agent that matters here (iPad,
// Android tablets, Kindle, PlayBook) also matches at least one of the phone
// patterns, so testing phones first would classify all of them as mobile.
//
// Android is the awkward case — Google's own rule is that a phone carries
// "Mobile" in the token and a tablet does not, so `Android` without `Mobile`
// is the tablet signal.
//
// iPadOS 13+ reports a desktop Safari user agent and is classified as desktop.
// `navigator.maxTouchPoints` would catch it (see useIsiPadUserAgent) but only
// in the browser, and a rule context that the server and the browser disagree
// on is worse than one that is consistently coarse — see below.
const TABLET_PATTERN = /iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i;

const MOBILE_PATTERN = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;

/**
 * Classifies a request's device from its user agent, for the `mobile` /
 * `tablet` / `desktop` rule contexts (CON-8651).
 *
 * Deliberately user-agent based rather than viewport based. `useScreen`
 * reports `isMobile: true` during SSR regardless of the real device, and a
 * rule context that disagrees between the server render and the browser would
 * have Algolia evaluate two different rule sets for the same page view.
 * The user agent is identical on both sides.
 *
 * Unknown or missing user agents fall through to `desktop` so that the request
 * always carries exactly one device context.
 *
 * @param {string} userAgent
 * @returns {'mobile'|'tablet'|'desktop'}
 */
const getDeviceRuleContext = userAgent => {
  if (typeof userAgent !== 'string' || !userAgent) {
    return ALGOLIA_RULE_CONTEXT.DESKTOP;
  }

  if (TABLET_PATTERN.test(userAgent)) {
    return ALGOLIA_RULE_CONTEXT.TABLET;
  }

  if (MOBILE_PATTERN.test(userAgent)) {
    return ALGOLIA_RULE_CONTEXT.MOBILE;
  }

  return ALGOLIA_RULE_CONTEXT.DESKTOP;
};

export default getDeviceRuleContext;
